import React, { useState, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import { 
  Bars3Icon, 
  XMarkIcon, 
  LanguageIcon, 
  HomeIcon, 
  BuildingOfficeIcon, 
  TruckIcon, 
  PhoneIcon,
  BookOpenIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../context/LanguageContext';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { language, changeLanguage, direction } = useLanguage();
  const [visible, setVisible] = useState(true);
  const [isTop, setIsTop] = useState(true); // <-- حالة جديدة لتتبع أعلى الصفحة
  // لا نحتاج lastScrollY كحالة (state) هنا، يمكن إدارته داخل useEffect
  // const [lastScrollY, setLastScrollY] = useState(0); 

  const toggleLanguage = () => {
    changeLanguage(language === 'ar' ? 'en' : 'ar');
  };

  const isRTL = direction === 'rtl';

  useEffect(() => {
    let previousScrollY = window.scrollY; // استخدام متغير محلي داخل التأثير

    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      const threshold = 10; // هامش صغير للسماح ببعض الحركة الطفيفة

      // 1. التعامل مع الشفافية
      if (currentScrollY <= threshold) {
        setIsTop(true);
      } else {
        setIsTop(false);
      }

      // 2. التعامل مع الإظهار والإخفاء (فقط عندما لا نكون في الأعلى)
      if (currentScrollY <= threshold) {
        // التأكد من أن الشريط مرئي دائماً في الأعلى
        setVisible(true);
      } else {
        // إخفاء عند النزول، إظهار عند الصعود
        if (currentScrollY > previousScrollY) {
          // Scrolling down
          setVisible(false);
        } else if (currentScrollY < previousScrollY) {
          // Scrolling up
          setVisible(true);
        }
        // إذا كان currentScrollY === previousScrollY (مثل التمرير الأفقي)، لا يتغير شيء
      }
      
      // تحديث قيمة التمرير السابقة للحدث القادم
      previousScrollY = currentScrollY;
    };

    // استدعاء الدالة عند تحميل المكون لتحديد الحالة الأولية
    controlNavbar();

    window.addEventListener('scroll', controlNavbar, { passive: true });
    
    // تنظيف المستمع عند إلغاء تحميل المكون
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, []); // <-- مصفوفة الاعتماديات فارغة لأننا ندير previousScrollY داخليًا

  return (
    <>
      <Disclosure 
        as="nav" 
        className={`
          fixed top-0 left-0 right-0 z-50 w-full 
          transition-all duration-300 ease-in-out 
          ${visible ? 'translate-y-0' : '-translate-y-full'} 
          ${isTop 
            ? 'bg-transparent backdrop-blur-none shadow-none border-b-0' 
            : 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200'
          }
        `}
      >
        {({ open, close }) => (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* --- باقي الكود الخاص بمحتوى الـ Navbar --- */}
              {/* لا حاجة لتغيير أي شيء هنا */}
              <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center justify-between h-20`}>
                {/* Logo - Centered */}
                <div className="flex-shrink-0 flex items-center justify-center order-2 mx-auto">
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/images/logo/nav_io.png" 
                      alt="Adwaa Al-Hijaz Logo" 
                      className="h-24 w-auto"
                      width="96"
                      height="96"
                      loading="eager"
                    />
                  </Link>
                </div>
                
                {/* Left Side Menu */}
                <div className={`hidden md:flex md:items-center ${isRTL ? 'order-3 ml-auto' : 'order-1 mr-auto'}`}>
                  <div className={`flex space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                    <Link to="/" className="text-gray-700 hover:text-emerald-600 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center hover:bg-emerald-50">
                      <HomeIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'الرئيسية' : 'Home'}
                    </Link>
                    <Link to="/hotels" className="text-gray-700 hover:text-emerald-600 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center hover:bg-emerald-50">
                      <BuildingOfficeIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'الفنادق' : 'Hotels'}
                    </Link>
                    <Link to="/packages" className="text-gray-700 hover:text-emerald-600 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center hover:bg-emerald-50">
                      <Squares2X2Icon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'الباقات' : 'Packages'}
                    </Link>
                  </div>
                </div>
                
                {/* Right Side Menu */}
                <div className={`hidden md:flex md:items-center ${isRTL ? 'order-1 mr-auto' : 'order-3 ml-auto'}`}>
                  <div className={`flex space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                    <Link to="/buses" className="text-gray-700 hover:text-emerald-600 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center hover:bg-emerald-50">
                      <TruckIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'وسائل النقل' : 'Transportation'}
                    </Link>
                    <Link to="/blog" className="text-gray-700 hover:text-emerald-600 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center hover:bg-emerald-50">
                      <BookOpenIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'المدونة' : 'Blog'}
                    </Link>
                    <Link to="/contact" className="text-gray-700 hover:text-emerald-600 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center hover:bg-emerald-50">
                      <PhoneIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'تواصل معنا' : 'Contact Us'}
                    </Link>
                  </div>
                  
                  {/* Language Switcher */}
                  <button
                    onClick={toggleLanguage}
                    className={`bg-gray-100 p-3 rounded-full text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 shadow-sm hover:shadow-md ${isRTL ? 'mr-4' : 'ml-4'}`}
                    aria-label={t('navbar.switchLanguage')}
                  >
                    <span className="sr-only">{t('navbar.switchLanguage')}</span>
                    <div className="flex items-center space-x-1">
                      <LanguageIcon className="h-5 w-5" aria-hidden="true" />
                      <span className="text-sm font-medium">{language === 'ar' ? 'EN' : 'عربي'}</span>
                    </div>
                  </button>
                </div>
                
                {/* Mobile menu button - positioned based on language */}
                <div className={`md:hidden flex items-center ${isRTL ? 'order-1 mr-0' : 'order-3 ml-0'}`}>
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500">
                    <span className="sr-only">{isRTL ? 'فتح القائمة' : 'Open main menu'}</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            {/* Mobile menu panel */}
            {/* لا حاجة لتغيير اللوحة المتنقلة، لكن يمكن إضافة خلفية وظل مشابهين إذا أردت */}
            <Disclosure.Panel className="md:hidden bg-white/95 backdrop-blur-md shadow-lg border-t border-gray-200">
              <div className="px-4 pt-4 pb-6 space-y-2">
                <Link 
                  to="/" 
                  className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                  onClick={() => close()}
                >
                  <HomeIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {isRTL ? 'الرئيسية' : 'Home'}
                </Link>
                <Link 
                  to="/hotels" 
                  className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                  onClick={() => close()}
                >
                  <BuildingOfficeIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {isRTL ? 'الفنادق' : 'Hotels'}
                </Link>
                <Link 
                  to="/packages" 
                  className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                  onClick={() => close()}
                >
                  <Squares2X2Icon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {isRTL ? 'الباقات' : 'Packages'}
                </Link>
                <Link 
                  to="/buses" 
                  className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                  onClick={() => close()}
                >
                  <TruckIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {isRTL ? 'وسائل النقل' : 'Transportation'}
                </Link>
                <Link 
                  to="/blog" 
                  className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                  onClick={() => close()}
                >
                  <BookOpenIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {isRTL ? 'المدونة' : 'Blog'}
                </Link>
                <Link 
                  to="/contact" 
                  className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                  onClick={() => close()}
                >
                  <PhoneIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {isRTL ? 'تواصل معنا' : 'Contact Us'}
                </Link>
                
                {/* Language Switcher in mobile menu */}
                <button
                  onClick={() => {
                    toggleLanguage();
                    close();
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 flex items-center transition-all duration-200"
                >
                  <LanguageIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} aria-hidden="true" />
                  <span>{isRTL ? 'English' : 'العربية'}</span>
                </button>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* هذا مهم لمنع المحتوى من القفز تحت الشريط الثابت */}
      <div className="h-20"></div> 
    </>
  );
};

export default Navbar;